import { canAssignEngineers, isEngineerRole } from '@veem/crm-schema'
import { productById } from '../data/catalog.ts'
import type { EngineeringRequest, ProtoOrder } from '../data/orders.ts'
import { personById } from '../data/people.ts'
import { compare, isPositive } from '../lib/decimal.ts'
import { TODAY } from '../lib/format.ts'
import { can } from '../lib/permissions.ts'
import { proto } from './store.ts'

export type EngineeringStep = 'drawings' | 'route' | 'materials' | 'approve'

export const STEP_LABELS: Record<EngineeringStep, string> = {
  drawings: 'Креслення',
  route: 'Маршрут',
  materials: 'Матеріали',
  approve: 'Затвердження',
}

export const STEPS: EngineeringStep[] = ['drawings', 'route', 'materials', 'approve']

export interface EngineeringTask {
  id: string
  order: ProtoOrder | null
  quantity: string
  request: EngineeringRequest
}

export const engineeringTasks = (): EngineeringTask[] => [
  ...proto.state.orders.flatMap((order) =>
    order.engineering
      ? [
          {
            id: order.deal.id,
            order,
            request: order.engineering,
            quantity:
              order.lines.find((line) => line.productId === order.engineering?.productId)
                ?.quantity ?? '1',
          },
        ]
      : [],
  ),
  ...proto.state.engineeringJobs.map((job) => ({
    id: job.id,
    order: null,
    request: job.request,
    quantity: job.quantity,
  })),
]

export const filesOf = (task: EngineeringTask) =>
  task.order?.files ?? proto.state.engineeringJobs.find((job) => job.id === task.id)?.files ?? []

/** The responsible engineer or one of the helpers on the same task. */
export const worksOn = (
  request: Pick<EngineeringRequest, 'engineerId' | 'helperIds'>,
  personId: string,
) => request.engineerId === personId || (request.helperIds ?? []).includes(personId)

export type EngineeringView = 'active' | 'done'
export const filteredEngineeringTasks = (view: EngineeringView, engineer?: string) =>
  engineeringTasks()
    .filter((task) => (view === 'done') === (task.request.state === 'done'))
    .filter(
      (task) =>
        !engineer ||
        (engineer === 'unassigned' ? !task.request.engineerId : worksOn(task.request, engineer)),
    )
    .sort((left, right) => {
      const rank = { high: 0, medium: 1, low: 2 }
      return (
        rank[left.request.priority] - rank[right.request.priority] ||
        left.request.dueAt.localeCompare(right.request.dueAt)
      )
    })

/** Ordinary tasks of the engineering staff: «Конструкторський» lists them with the jobs above. */
export const departmentTasks = (view: EngineeringView, engineer?: string) =>
  proto.state.tasks
    .filter(
      (task) =>
        isEngineerRole(personById(task.assigneeId).role) &&
        task.status === (view === 'done' ? 'completed' : 'open') &&
        (!engineer || task.assigneeId === engineer),
    )
    .sort((left, right) => (left.dueAt ?? '9').localeCompare(right.dueAt ?? '9'))

export const engineeringSourceLabel = (task: EngineeringTask) => {
  const source = task.request.source
  return source.kind === 'monthly'
    ? `Місячний план ${source.month}`
    : source.kind === 'manual'
      ? 'Окрема робота'
      : 'Замовлення'
}

export const engineeringContext = (task: EngineeringTask) =>
  task.order?.deal.clientName ??
  (task.request.source.kind === 'monthly'
    ? `Місячний план ${task.request.source.month}`
    : 'Окрема робота')

export const engineeringTaskById = (id: string) => engineeringTasks().find((task) => task.id === id)

export const drawingsOf = (task: EngineeringTask) =>
  filesOf(task).filter((file) => file.kind === 'drawing')

/** Drawings count once an engineer has uploaded one; the client's sketch alone is not enough. */
export const engineerDrawings = (task: EngineeringTask) =>
  drawingsOf(task).filter((file) => isEngineerRole(personById(file.uploadedById).role))

export const bomIsValid = (task: EngineeringTask) =>
  task.request.bom.length > 0 &&
  task.request.bom.every(
    (line) =>
      isPositive(line.perUnit) &&
      compare(line.waste, '1') >= 0 &&
      task.request.route.includes(line.stageKey) &&
      proto.state.materials.some((material) => material.id === line.materialId),
  )

export const stepDone = (task: EngineeringTask, step: EngineeringStep): boolean => {
  switch (step) {
    case 'drawings':
      return engineerDrawings(task).length > 0
    case 'route':
      return task.request.route.length > 0
    case 'materials':
      return bomIsValid(task)
    case 'approve':
      return task.request.state === 'done'
  }
}

/** The first unfinished step: what the engineer should do now. */
export const currentStep = (task: EngineeringTask): EngineeringStep | null =>
  STEPS.find((step) => !stepDone(task, step)) ?? null

export const readyToApprove = (task: EngineeringTask) =>
  task.request.state !== 'done' &&
  !proto.actions.engineeringReleaseConflict(task.id) &&
  stepDone(task, 'drawings') &&
  stepDone(task, 'route') &&
  stepDone(task, 'materials')

export const quantityOf = (task: EngineeringTask) => task.quantity

export const productOf = (task: EngineeringTask) => productById(task.request.productId)

export const isLate = (task: EngineeringTask) =>
  task.request.state !== 'done' && task.request.dueAt.slice(0, 10) < TODAY

/** Orders with a product that has no технологічна карта and no engineering task yet. */
export const ordersNeedingEngineering = () =>
  proto.state.orders.filter(
    (order) =>
      !order.engineering &&
      order.deal.stageCategory !== 'unsuccessful' &&
      order.lines.some((line) => {
        const product = productById(line.productId)
        return product.kind === 'product' && product.bomVersion === null
      }),
  )

/** Engineers and admins draft and approve; the assigned engineer is the one expected to. */
export const canEngineer = (task?: EngineeringTask) =>
  can(proto.role(), 'projects', 'update') &&
  (!task || proto.role() === 'admin' || worksOn(task.request, proto.personId()))

/** The client's rule: a sales manager or production asks, and picks the engineer. */
export const canRequestEngineering = () => can(proto.role(), 'engineering', 'create')

/**
 * «Створити завдання конструктору» on one order: only on request, one task per order. Sales may
 * ask only on their own orders; the server checks the same.
 */
export const canRequestEngineeringFor = (order: ProtoOrder) =>
  canRequestEngineering() &&
  !order.engineering &&
  order.deal.stageCategory !== 'unsuccessful' &&
  (proto.role() !== 'sales' || order.deal.managerId === proto.personId()) &&
  order.lines.some((line) => productById(line.productId).kind === 'product')

/** The administrator and every engineer hand out work. */
export const canAssignEngineer = (_task?: EngineeringTask) => canAssignEngineers(proto.role())
